import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Building2, Cloud, MapPin, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Checkbox } from '@/components/ui/checkbox';

type WizardStep = 'org' | 'cloud' | 'infrastructure' | 'complete';

// Helper function to get default coordinates for a country (capital city)
const getDefaultCoordinates = (countryCode: string): { lat: number; lon: number } => {
  const coordinates: Record<string, { lat: number; lon: number }> = {
    'SE': { lat: 59.3293, lon: 18.0686 }, // Stockholm
    'NO': { lat: 59.9139, lon: 10.7522 }, // Oslo
    'DK': { lat: 55.6761, lon: 12.5683 }, // Copenhagen
    'FI': { lat: 60.1699, lon: 24.9384 }, // Helsinki
    'DE': { lat: 52.5200, lon: 13.4050 }, // Berlin
    'NL': { lat: 52.3676, lon: 4.9041 },  // Amsterdam
    'BE': { lat: 50.8503, lon: 4.3517 },  // Brussels
    'FR': { lat: 48.8566, lon: 2.3522 },  // Paris
    'ES': { lat: 40.4168, lon: -3.7038 }, // Madrid
    'IT': { lat: 41.9028, lon: 12.4964 }, // Rome
    'PL': { lat: 52.2297, lon: 21.0122 }, // Warsaw
    'CZ': { lat: 50.0755, lon: 14.4378 }, // Prague
    'AT': { lat: 48.2082, lon: 16.3738 }, // Vienna
    'CH': { lat: 46.9480, lon: 7.4474 },  // Bern
    'UK': { lat: 51.5074, lon: -0.1278 }, // London
    'GB': { lat: 51.5074, lon: -0.1278 }, // London (alternative code)
  };
  
  return coordinates[countryCode] || { lat: 50.8503, lon: 4.3517 }; // Default to Brussels
};

export default function OnboardingPage() {
  const { signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('org');
  const [orgId, setOrgId] = useState<string>('');
  
  // Wizard data
  const [cloudProviders, setCloudProviders] = useState<string[]>([]);
  const [estimatedSites, setEstimatedSites] = useState<number>(1);

  const handleGoBack = async () => {
    await signOut();
    navigate('/');
  };

  const [country, setCountry] = useState('SE');
  const [timezone, setTimezone] = useState('Europe/Stockholm');

  const handleSetupOrganization = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const orgName = formData.get('orgName') as string;

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Check if org already exists
      const { data: existingOrg } = await supabase
        .from('orgs')
        .select('*')
        .eq('name', orgName)
        .maybeSingle();

      let createdOrgId: string;

      if (existingOrg) {
        createdOrgId = existingOrg.id;
      } else {
        // Generate UUID for the org
        const newOrgId = crypto.randomUUID();
        
        // Create new organization with explicit ID
        const insertResult = await supabase
          .from('orgs')
          .insert({
            id: newOrgId,
            name: orgName,
            country,
            timezone,
            language: 'sv-SE'
          });
        
        if (insertResult.error) throw insertResult.error;

        createdOrgId = newOrgId;
      }

      // Update user profile with org_id (role is handled by handle_new_user trigger)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ org_id: createdOrgId })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      // Create default NIS2 standard if it doesn't exist
      const { error: standardError } = await supabase
        .from('standards')
        .upsert({
          key: 'NIS2',
          name: 'Network and Information Security Directive 2',
          enabled: true,
          org_id: createdOrgId
        }, {
          onConflict: 'org_id,key',
          ignoreDuplicates: true
        });

      if (standardError && !standardError.message.includes('duplicate')) {
        console.error('Standard creation warning:', standardError);
      }

      toast({
        title: "Organization created!",
        description: "Let's set up your infrastructure.",
      });

      // Invalidate profile query to refresh with new org and role
      queryClient.invalidateQueries({ queryKey: ["profile"] });

      // Store org ID and move to next step
      setOrgId(createdOrgId);
      setCurrentStep('cloud');
      
    } catch (error: any) {
      toast({
        title: "Error setting up organization",
        description: error.message || 'Please try again',
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };

  const handleCloudSetup = async () => {
    setIsLoading(true);
    try {
      // Create cloud services based on selected providers
      if (cloudProviders.length > 0 && orgId) {
        const cloudServicesToCreate = cloudProviders.map(provider => ({
          org_id: orgId,
          name: `${provider} Environment`,
          provider: provider,
          environment: 'Production',
          criticality: 'High'
        }));

        const { error } = await supabase
          .from('cloud_services')
          .insert(cloudServicesToCreate);

        if (error) {
          toast({
            title: "Warning: Cloud services not saved",
            description: error.message || "Some cloud services may not have been created. You can add them later.",
            variant: "destructive",
          });
        }
      }

      setCurrentStep('infrastructure');
    } catch (error: any) {
      toast({
        title: "Error saving cloud setup",
        description: error.message,
        variant: "destructive",
      });
    }
    setIsLoading(false);
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      // CREATE SITES based on estimatedSites
      if (estimatedSites > 0 && orgId) {
        const baseCoords = getDefaultCoordinates(country);
        
        const sitesToCreate = Array.from({ length: estimatedSites }, (_, i) => {
          // Calculate offset for multiple sites to prevent stacking
          const latOffset = i % 2 === 1 ? Math.floor((i + 1) / 2) * 0.01 : 0;
          const lonOffset = i % 2 === 0 && i > 0 ? Math.floor(i / 2) * 0.01 : 0;
          
          return {
            org_id: orgId,
            name: `Site ${i + 1}`,
            site_code: `SITE-${String(i + 1).padStart(3, '0')}`,
            criticality: 'Medium' as const,
            country: country,
            timezone: timezone,
            metadata: {
              coordinates: {
                lat: baseCoords.lat + latOffset,
                lon: baseCoords.lon + lonOffset
              },
              notes: 'Created during initial setup - Please update location and details'
            }
          };
        });

        const { error: sitesError } = await supabase
          .from('sites')
          .insert(sitesToCreate);

        if (sitesError) {
          console.error('Sites creation error:', sitesError);
          toast({
            title: "Warning: Sites not created",
            description: "You can add sites manually later.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Sites created",
            description: `${estimatedSites} site(s) created successfully.`,
          });
        }
      }

      // Invalidate and refetch all queries to get fresh data
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      await queryClient.invalidateQueries({ queryKey: ['organization'] });
      await queryClient.refetchQueries({ queryKey: ['profile'] });
      
      // Small delay to ensure queries complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Set completion step briefly
      setCurrentStep('complete');
      
      // Then redirect to dashboard
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1500);
    } catch (error: any) {
      console.error('Completion error:', error);
      toast({
        title: "Error completing setup",
        description: error.message,
        variant: "destructive",
      });
    }
    setIsLoading(false);
  };

  const toggleCloudProvider = (provider: string) => {
    setCloudProviders(prev => 
      prev.includes(provider) 
        ? prev.filter(p => p !== provider)
        : [...prev, provider]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <button 
              onClick={handleGoBack}
              className="bg-white/10 p-3 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
              title="Go back to homepage"
            >
              <Shield className="h-8 w-8 text-white" />
            </button>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome to ComplyManager</h1>
          <p className="text-white/80">
            {currentStep === 'org' && 'Set up your organization to get started'}
            {currentStep === 'cloud' && 'Tell us about your cloud infrastructure'}
            {currentStep === 'infrastructure' && 'Tell us about your physical infrastructure'}
            {currentStep === 'complete' && 'Setup complete!'}
          </p>
        </div>

        {/* Progress indicator */}
        {currentStep !== 'complete' && (
          <div className="flex justify-center gap-2 mb-6">
            <div className={`h-2 w-16 rounded-full ${currentStep === 'org' ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`h-2 w-16 rounded-full ${currentStep === 'cloud' ? 'bg-white' : 'bg-white/30'}`} />
            <div className={`h-2 w-16 rounded-full ${currentStep === 'infrastructure' ? 'bg-white' : 'bg-white/30'}`} />
          </div>
        )}

        {/* Step 1: Organization Setup */}
        {currentStep === 'org' && (
          <Card className="bg-white/95 backdrop-blur shadow-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Organization Setup
              </CardTitle>
              <CardDescription>
                Create your compliance workspace for NIS2 management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSetupOrganization} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name *</Label>
                  <Input
                    id="orgName"
                    name="orgName"
                    type="text"
                    placeholder="Enter your organization name"
                    required
                    disabled={isLoading}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select value={country} onValueChange={setCountry} disabled={isLoading}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SE">Sweden (SE)</SelectItem>
                      <SelectItem value="NO">Norway (NO)</SelectItem>
                      <SelectItem value="DK">Denmark (DK)</SelectItem>
                      <SelectItem value="FI">Finland (FI)</SelectItem>
                      <SelectItem value="DE">Germany (DE)</SelectItem>
                      <SelectItem value="NL">Netherlands (NL)</SelectItem>
                      <SelectItem value="BE">Belgium (BE)</SelectItem>
                      <SelectItem value="FR">France (FR)</SelectItem>
                      <SelectItem value="ES">Spain (ES)</SelectItem>
                      <SelectItem value="IT">Italy (IT)</SelectItem>
                      <SelectItem value="PL">Poland (PL)</SelectItem>
                      <SelectItem value="CZ">Czech Republic (CZ)</SelectItem>
                      <SelectItem value="AT">Austria (AT)</SelectItem>
                      <SelectItem value="CH">Switzerland (CH)</SelectItem>
                      <SelectItem value="UK">United Kingdom (UK)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone} disabled={isLoading}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Europe/Stockholm">Europe/Stockholm (CET)</SelectItem>
                      <SelectItem value="Europe/Oslo">Europe/Oslo (CET)</SelectItem>
                      <SelectItem value="Europe/Copenhagen">Europe/Copenhagen (CET)</SelectItem>
                      <SelectItem value="Europe/Helsinki">Europe/Helsinki (EET)</SelectItem>
                      <SelectItem value="Europe/Berlin">Europe/Berlin (CET)</SelectItem>
                      <SelectItem value="Europe/Amsterdam">Europe/Amsterdam (CET)</SelectItem>
                      <SelectItem value="Europe/Brussels">Europe/Brussels (CET)</SelectItem>
                      <SelectItem value="Europe/Paris">Europe/Paris (CET)</SelectItem>
                      <SelectItem value="Europe/Madrid">Europe/Madrid (CET)</SelectItem>
                      <SelectItem value="Europe/Rome">Europe/Rome (CET)</SelectItem>
                      <SelectItem value="Europe/Warsaw">Europe/Warsaw (CET)</SelectItem>
                      <SelectItem value="Europe/Prague">Europe/Prague (CET)</SelectItem>
                      <SelectItem value="Europe/Vienna">Europe/Vienna (CET)</SelectItem>
                      <SelectItem value="Europe/Zurich">Europe/Zurich (CET)</SelectItem>
                      <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Creating...' : 'Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Cloud Setup */}
        {currentStep === 'cloud' && (
          <Card className="bg-white/95 backdrop-blur shadow-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cloud className="h-5 w-5" />
                Cloud Services
              </CardTitle>
              <CardDescription>
                Which cloud providers do you use? (Select all that apply)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {['AWS', 'Azure', 'GCP', 'Oracle Cloud', 'Alibaba Cloud', 'IBM Cloud'].map((provider) => (
                  <div
                    key={provider}
                    onClick={() => toggleCloudProvider(provider)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      cloudProviders.includes(provider)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={cloudProviders.includes(provider)}
                        onCheckedChange={() => toggleCloudProvider(provider)}
                      />
                      <span className="font-medium">{provider}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep('org')}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleCloudSetup}
                  disabled={isLoading}
                  className="flex-1"
                >
                  {isLoading ? 'Saving...' : 'Continue'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Infrastructure Setup */}
        {currentStep === 'infrastructure' && (
          <Card className="bg-white/95 backdrop-blur shadow-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Physical Infrastructure
              </CardTitle>
              <CardDescription>
                Tell us about your physical sites and infrastructure
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="estimatedSites">How many physical sites/locations do you have?</Label>
                <Input
                  id="estimatedSites"
                  type="number"
                  min="1"
                  max="9999"
                  value={estimatedSites}
                  onChange={(e) => setEstimatedSites(Number(e.target.value))}
                  placeholder="Enter number of sites"
                  disabled={isLoading}
                />
              </div>

              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  You can add and configure your sites, zones, and systems after completing this setup.
                </p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep('cloud')}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleComplete}
                  disabled={isLoading}
                  className="flex-1"
                >
                  {isLoading ? 'Completing...' : 'Complete Setup'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Complete */}
        {currentStep === 'complete' && (
          <Card className="bg-white/95 backdrop-blur shadow-elevated">
            <CardContent className="py-12">
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <CheckCircle2 className="h-16 w-16 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold">Setup Complete!</h2>
                <p className="text-muted-foreground">
                  Redirecting you to your dashboard...
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}